import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Check, Crown, Trophy, Globe, Film, Play, Shield, Zap } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stripePromise = loadStripe('pk_test_51QnbF7BAjZYDLUqwZpZM0O2pYn2VvLXbKQgMB3JYqtYH4qF3vYV7JkXsYpCx3YiQ4vvdGH8kJGvPQHwR0Yd0Sy6G00YC2hjvxF');

const plans = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 9.99,
    period: 'month',
    features: [
      'Unlimited streaming',
      'HD quality',
      'All live matches',
      'Originals & documentaries',
      'Multi-device access'
    ]
  },
  {
    id: 'yearly',
    name: 'Annual',
    price: 99,
    period: 'year',
    popular: true,
    savings: 'Save $21',
    features: [
      'Everything in Monthly',
      'Priority support',
      'Early access to new content',
      'Exclusive behind-the-scenes',
      'Best price guarantee'
    ]
  }
];

const unlockFeatures = [
  {
    icon: Trophy,
    title: 'AFCON & CAF Competitions',
    description: "Watch Africa's biggest international tournaments live.",
    color: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/20',
  },
  {
    icon: Globe,
    title: 'Domestic African Leagues',
    description: 'NPFL \u2022 PSL \u2022 Botola Pro \u2022 Tanzania Premier League.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10 border-emerald-500/20',
  },
  {
    icon: Film,
    title: 'AfroSports Originals',
    description: 'Exclusive documentaries and player stories.',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10 border-blue-500/20',
  },
  {
    icon: Play,
    title: 'Highlights & Match Replays',
    description: 'Catch every goal and key moment.',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10 border-rose-500/20',
  },
];

const CheckoutForm = ({ clientSecret, amount, planType }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (clientSecret === 'demo_secret_for_mvp_testing') {
      setLoading(true);
      toast.info('Demo Mode: Subscription activated without payment processing');
      const token = localStorage.getItem('token');
      try {
        await axios.post(
          `${API}/payments/confirm?payment_intent_id=demo_intent`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.log('Demo mode - skipping confirmation');
      }
      setTimeout(() => {
        toast.success('Subscription activated!');
        navigate('/dashboard');
      }, 1500);
      return;
    }

    if (!stripe || !elements) return;
    setLoading(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required'
      });

      if (error) {
        toast.error(error.message);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/payments/confirm?payment_intent_id=${paymentIntent.id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Subscription activated!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment processing failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {clientSecret !== 'demo_secret_for_mvp_testing' && (
        <div className="glass-effect rounded-xl p-6">
          <PaymentElement />
        </div>
      )}
      {clientSecret === 'demo_secret_for_mvp_testing' && (
        <div className="glass-effect rounded-xl p-6 text-center">
          <p className="text-white mb-2">Demo Mode Active</p>
          <p className="text-sm text-muted-foreground">Click below to activate subscription without payment</p>
        </div>
      )}
      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-emerald-600 text-white hover:bg-emerald-700 font-bold text-lg h-12 rounded-full uppercase tracking-wider"
        data-testid="confirm-payment-button"
      >
        {loading ? 'Processing...' : clientSecret === 'demo_secret_for_mvp_testing' ? 'Activate Subscription' : `Pay $${(amount / 100).toFixed(2)}`}
      </Button>
    </form>
  );
};

export default function Subscription() {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');

  const handleSelectPlan = async (planId) => {
    if (!token) {
      toast.error('Please login to subscribe');
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/payments/create-subscription-intent`,
        { subscription_type: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClientSecret(response.data.client_secret);
      setSelectedPlan({ id: planId, amount: response.data.amount });
    } catch (error) {
      console.error('Error creating payment intent:', error);
      toast.error('Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e]">
      <Navbar />

      {!clientSecret ? (
        <>
          {/* HERO */}
          <section className="relative pt-28 pb-16 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/15 via-transparent to-transparent" />
            <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-600/5 rounded-full blur-3xl" />
            <div className="absolute top-40 right-1/4 w-72 h-72 bg-emerald-600/5 rounded-full blur-3xl" />
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative max-w-4xl mx-auto px-4 text-center"
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-semibold mb-6">
                <Zap className="w-4 h-4" /> AfroSports Premium
              </div>
              <h1 data-testid="subscription-title" className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-tight">
                Watch African football live —{' '}
                <span className="text-emerald-400">anywhere in the world</span>
              </h1>
              <p className="mt-5 text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
                Stream AFCON, domestic leagues, originals and more. One subscription, every match.
              </p>
            </motion.div>
          </section>

          {/* WHAT YOU UNLOCK */}
          <section className="py-12 border-t border-white/5" data-testid="unlock-features">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="text-lg font-bold text-white text-center mb-8"
              >
                What You Unlock With AfroSports
              </motion.h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {unlockFeatures.map((f, i) => (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + i * 0.08 }}
                    data-testid={`feature-tile-${i}`}
                    className={`rounded-xl border p-5 ${f.bg} transition-all hover:scale-[1.03] hover:shadow-lg`}
                  >
                    <f.icon className={`w-8 h-8 ${f.color} mb-3`} />
                    <h3 className="text-white font-bold text-sm mb-1.5">{f.title}</h3>
                    <p className="text-gray-400 text-xs leading-relaxed">{f.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* PRICING PLANS */}
          <section className="py-12" data-testid="pricing-section">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-lg font-bold text-white text-center mb-8"
              >
                Choose Your Plan
              </motion.h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {plans.map((plan, idx) => (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 + idx * 0.1 }}
                    data-testid={`plan-${plan.id}`}
                    className={`relative rounded-2xl p-8 bg-[#111a30] border transition-all hover:shadow-xl ${
                      plan.popular
                        ? 'border-emerald-500 shadow-emerald-500/10 shadow-lg'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <span className="bg-emerald-600 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${plan.popular ? 'bg-emerald-600' : 'bg-white/10'}`}>
                        <Crown className={`w-7 h-7 ${plan.popular ? 'text-white' : 'text-emerald-400'}`} />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-black text-white">${plan.price}</span>
                        <span className="text-gray-500 text-sm">/{plan.period}</span>
                      </div>
                      {plan.savings && (
                        <p className="text-emerald-400 text-sm font-bold mt-1.5">{plan.savings}</p>
                      )}
                    </div>

                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, fi) => (
                        <li key={fi} className="flex items-start gap-2.5">
                          <div className="w-5 h-5 bg-emerald-500/15 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-emerald-400" />
                          </div>
                          <span className="text-gray-300 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className={`w-full font-bold h-12 rounded-full text-sm uppercase tracking-wider transition-all ${
                        plan.popular
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                          : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
                      }`}
                      onClick={() => handleSelectPlan(plan.id)}
                      disabled={loading}
                      data-testid={`select-${plan.id}-button`}
                    >
                      {loading ? 'Loading...' : 'Get Started'}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* FREE TRIAL + TRUST */}
          <section className="py-10 border-t border-white/5">
            <div className="max-w-2xl mx-auto px-4 text-center space-y-4">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                data-testid="free-trial-message"
                className="text-white font-semibold text-base"
              >
                7-day free trial. Cancel anytime.
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.55 }}
                data-testid="trust-signal"
                className="flex items-center justify-center gap-2 text-gray-500 text-sm"
              >
                <Shield className="w-4 h-4 text-emerald-500" />
                Secure payments powered by Stripe
              </motion.div>
            </div>
          </section>
        </>
      ) : (
        /* CHECKOUT */
        <div className="pt-24 pb-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8"
          >
            <div className="mb-8">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/10 mb-4"
                onClick={() => { setClientSecret(null); setSelectedPlan(null); }}
                data-testid="back-to-plans-button"
              >
                &larr; Back to plans
              </Button>
              <h1 className="text-3xl font-black text-white mb-2">COMPLETE PAYMENT</h1>
              <p className="text-gray-400">
                {clientSecret === 'demo_secret_for_mvp_testing'
                  ? 'Demo mode — No payment required'
                  : 'Secure payment powered by Stripe'}
              </p>
            </div>

            {clientSecret === 'demo_secret_for_mvp_testing' ? (
              <Elements stripe={stripePromise}>
                <CheckoutForm clientSecret={clientSecret} amount={selectedPlan.amount} planType={selectedPlan.id} />
              </Elements>
            ) : (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CheckoutForm clientSecret={clientSecret} amount={selectedPlan.amount} planType={selectedPlan.id} />
              </Elements>
            )}

            {clientSecret !== 'demo_secret_for_mvp_testing' && (
              <p className="text-center text-sm text-gray-500 mt-6">
                Test card: 4242 4242 4242 4242 | Any future date | Any 3 digits
              </p>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}